import { Message, Model } from '../../types/mfc'
import { getRoomState, setRoomMessagesState } from '../app-state/state.room'
import { MfcUserNotepad } from '../libraries/mfc/MfcUserNotepad'
import {
    buildTipPayloadOptions,
    sendShareTip,
    sendTip,
    sendTipRepeatedly,
} from '../mfcLogic/api/tip'
import { FCTYPE_CMESG, FCTYPE_PMESG } from '../mfcLogic/mfcConstants'
import {
    getTokenPurchaseInfo,
    shiftTokenStorage,
    tokensQualifyPurchase,
} from '../mfcLogic/tokens'
import { toTokens } from '../mfcLogic/toTokens'
import {
    isLevelPremiumOrHigher,
    UserAccess,
} from '../mfcLogic/userProperties/level'
import { isLounge } from '../mfcLogic/userProperties/user'
import { MODALSTATE, RoomView } from '../state/modals'
import { openChatCommandsDrawer } from '../state/modals/state.drawer'
import { isQuickReplyOpen } from '../state/modals/state.floater'
import { APPSTATE } from '../state/state.app'
import { setPMChatTextState } from '../state/state.pm'
import { AppRouter, HISTORY } from '../state/state.router'
import { idToSlug, slugToID, USERSSTATE } from '../state/state.users'
import i18n from '../utilities/i18n'
import { routerPush } from '../utilities/router'
import { getNextOrPreviousModel } from './getNextOrPreviousModel'
import { inChatQuickBuyDialog } from './inChatQuickBuyDialog'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { openMessage } from './message'
import { goToHome } from './nav'
import { saveNote } from './notes'
import { openError } from './openError'
import {
    openRoom,
    setLoungeSurrogate,
    setNextSurrogate,
    setPreviousSurrogate,
} from './room'
import { sendPrivateMessage } from './sendPrivateMessage'
import { sendRoomMessage } from './sendRoomMessage'

type CommandPayload = {
    id?: number
    activeChat: Model
    args?: Array<string>
    chatType?: number
    isPremium?: boolean
    mainCommand?: string
    message?: string
}
type SyncCommand = (payload: CommandPayload) => void
type AsyncCommand = (payload: CommandPayload) => Promise<void>
type Command = SyncCommand | AsyncCommand
type Commands = Record<string, Command>

const COMMANDS: Commands = {
    '/?': helpCmd,
    '/help': helpCmd,
    '/awaymsg': noCmd,
    '/back': noCmd,
    '/h': homepageCmd,
    '/homepage': homepageCmd,
    '/l': lastCmd,
    '/last': lastCmd,
    '/n': nextCmd,
    '/next': nextCmd,
    '/p': prevCmd,
    '/prev': prevCmd,
    '/pm': pmCmd,
    '/w': whisperCmd,
    '/whisper': whisperCmd,
    '/me': meCmd,
    '/em': meCmd,
    '/sar': sarcasmCmd,
    '/sarcasm': sarcasmCmd,
    '/sarcastic': sarcasmCmd,
    '/note': noteCmd,
    '/note4': noteForCmd,
    '/notefor': noteForCmd,
    '/tip': tipCmd,
    '/tip4': tipForCmd,
    '/tipfor': tipForCmd,
    '/sharetip': shareTipCmd,
    '/qb': quickBuyCmd,
    '/quickbuy': quickBuyCmd,
    '/ts': toStorageCmd,
    '/tostorage': toStorageCmd,
    '/fs': fromStorageCmd,
    '/fromstorage': fromStorageCmd,
}

export const generateChatCommands = async (
    message: string,
    user: Model,
    chatType: number,
) => {
    const { level } = USERSSTATE[slugToID['me']]
    const isPremium = isLevelPremiumOrHigher(level)
    const args = message.split(' ')
    const mainCommand = args[0]
    const cmd = COMMANDS[mainCommand]
    const payload: CommandPayload = {
        activeChat: user,
        args,
        chatType,
        isPremium,
        mainCommand,
        message,
    }
    if (!cmd) {
        return defaultCmd(payload)
    }
    return cmd(payload)
}

function helpCmd() {
    openChatCommandsDrawer()
}

function noCmd({ activeChat, mainCommand }: CommandPayload) {
    commandConfirmation({
        id: activeChat.id,
        error: `The ${mainCommand} command is not currently supported.`,
    })
}

async function defaultCmd({ args, activeChat }: CommandPayload) {
    if (args.length !== 1) {
        commandConfirmation({ id: activeChat.id, error: 'Unknown command.' })
        return
    }
    const slug = args[0].slice(1).toLowerCase()
    const user = await loadUserIfNeeded(slug)
    if (user && user.level !== UserAccess.MODEL) return
    return openRoom(slug)
}

function homepageCmd() {
    goToHome()
}

async function lastCmd({ activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id, isLounge, slug: loungeSlug } = activeChat
    const { [id]: room } = getRoomState()
    if (!room) {
        commandConfirmation({ id: activeChat.id, error: 'Command failed.' })
        return
    }
    const { previousLoungeSurrogateID } = room
    if (isLounge) {
        setLoungeSurrogate(previousLoungeSurrogateID, loungeSlug)
        return
    }
    // const { previousURL: url } = NAVSTATE
    const [{ visibleRoute: url }] = HISTORY.lastRoute
    if (!url) {
        commandConfirmation({ id: activeChat.id, error: 'No previous room.' })
        return
    }
    if (!url.startsWith('/room')) {
        routerPush(url)
        return
    }
    const [, , roomSlug] = url.split('/')
    if (roomSlug) {
        return openRoom(roomSlug)
    }
}

async function nextCmd({ activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id, isLounge } = activeChat
    if (isLounge) {
        setNextSurrogate(id)
        return
    }
    const nextId = getNextOrPreviousModel(id)
    return openRoom(nextId)
}

async function prevCmd({ activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id, isLounge } = activeChat
    if (isLounge) {
        setPreviousSurrogate(id)
        return
    }
    const prevId = getNextOrPreviousModel(id, { previous: true })
    return openRoom(prevId)
}

async function pmCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: i18n.t('chat.pmWarningIfNotPremium') })
        return
    }
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug) {
        commandConfirmation({
            id: activeChat.id,
            error: i18n.t('chat.pmWithoutUsername'),
        })
        return
    }
    // const { asPath } = Router.router
    if (AppRouter.route.visiblePage === 'message') {
        commandConfirmation({
            id: activeChat.id,
            error: i18n.t('chat.pmFromPmWarning'),
        })
        return
    }
    const user = await loadUserIfNeeded(slug)
    if (!user) {
        commandConfirmation({
            id: activeChat.id,
            error: i18n.t('chat.pmWithoutValidUsername'),
        })
        return
    }
    if (args.length < 2) return
    args.shift()
    const id = slugToID[slug]
    const message = args.join(' ')
    const onRouteChangeComplete = () => {
        // this setTimeout is needed to avoid a race condition with the message opening
        setTimeout(() => {
            sendPrivateMessage(id, message, true)
        }, 200)
        AppRouter.events.offCompleted(onRouteChangeComplete)
    }
    AppRouter.events.onCompleted(onRouteChangeComplete)
    // wait for the message to open before sending the message to allow history to load
    await openMessage(slug)
}

async function whisperCmd({
    args,
    chatType,
    activeChat,
    isPremium,
}: CommandPayload) {
    if (!isPremium) {
        openError({ message: i18n.t('chat.pmWarningIfNotPremium') })
        return
    }
    // const { asPath } = Router.router
    if (AppRouter.route.visiblePage === 'message') {
        commandConfirmation({
            id: activeChat.id,
            error: i18n.t('chat.whisperFromPmWarning'),
        })
        return
    }
    if (chatType !== FCTYPE_CMESG) return
    const { isLounge } = activeChat
    if (isLounge) {
        openError({ message: i18n.t('chat.whisperFromLoungeWarning') })
        return
    }
    if (args.length < 2) return
    args.shift()
    const message = args.join(' ')
    return sendPrivateMessage(activeChat.id, message, true)
}

async function meCmd({ args, activeChat, chatType }: CommandPayload) {
    if (args.length < 2) return
    args.shift()
    const { id } = activeChat
    const message = args.join(' ')
    if (chatType === FCTYPE_CMESG) {
        return sendRoomMessage(id, '{*emote*} ' + message)
    } else if (chatType === FCTYPE_PMESG) {
        return sendPrivateMessage(id, '{*emote*} ' + message)
    }
}

async function sarcasmCmd({ args, activeChat, chatType }: CommandPayload) {
    if (args.length < 2) return
    args.shift()
    const { id } = activeChat
    const message = args.join(' ')
    if (chatType === FCTYPE_CMESG) {
        return sendRoomMessage(id, '*sarcasm* ' + message)
    } else if (chatType === FCTYPE_PMESG) {
        return sendPrivateMessage(id, '*sarcasm* ' + message)
    }
}

async function noteCmd({ args, id, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({ message: i18n.t('chat.noteCommandWithoutPermission') })
        return
    }
    const resolvedId = id ?? activeChat.id
    const inLounge = id ? false : isLounge(activeChat.id)
    const userId =
        id ??
        (!inLounge
            ? activeChat.id
            : getRoomState()[resolvedId]?.loungeSurrogateID)
    const { token } = APPSTATE.session
    if (args.length === 0 || !token || !userId) {
        commandConfirmation({ id: activeChat.id, error: 'Note not saved.' })
        return
    }
    const date = MfcUserNotepad.generateEntryDate()
    const note = date + '\n' + args.slice(1).join(' ')
    return saveNote(token, userId, note)
        .then(() => {
            commandConfirmation({
                id: activeChat.id,
                message: 'Note saved successfully.',
            })
        })
        .catch(() => {
            commandConfirmation({
                id: activeChat.id,
                error: 'Note not saved.',
            })
        })
}

async function noteForCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: i18n.t('chat.noteCommandWithoutPermission') })
        return
    }
    // grab the model name from the second word
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a username.',
        })
        return
    }
    const user = await loadUserIfNeeded(slug)
    if (!user) {
        commandConfirmation({ id: activeChat.id, error: 'User not found.' })
        return
    }
    const { id } = user
    return noteCmd({ ...payload, id })
}

async function tipCmd({
    args,
    slug,
    activeChat,
    isPremium,
    isTipFor,
}: CommandPayload & { slug?: string; isTipFor?: boolean }) {
    if (!isPremium) {
        openError({ message: i18n.t('chat.tipCommandWithoutPermission') })
        return
    }
    const resolvedSlug = slug ?? activeChat.slug ?? idToSlug[activeChat.id]
    const inLounge = slug ? false : isLounge(activeChat.id)
    const target =
        slug ??
        (!inLounge
            ? resolvedSlug
            : idToSlug[
                  getRoomState()[slugToID[resolvedSlug]]?.loungeSurrogateID
              ])
    if (!target) {
        commandConfirmation({ id: activeChat.id, error: 'Tip not sent.' })
        return
    }
    const { amount, error, payloadOptions, repetitionCount, delay } =
        buildTipPayloadOptions(args)
    if (Number.isNaN(amount) || amount < 1) {
        const error = 'You must include a positive token amount.'
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    if (error) {
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    if (repetitionCount) {
        const callback = (args: { message?: string; error?: string } = {}) =>
            commandConfirmation({ id: activeChat.id, tip: true, ...args })
        return sendTipRepeatedly(
            target,
            amount,
            payloadOptions,
            repetitionCount,
            delay,
            callback,
        )
    }
    return sendTip(target, amount, payloadOptions)
        .then(async () => {
            const user = await loadUserIfNeeded(target)
            const username = user?.name ?? target
            const targetStr = isTipFor ? `${username} ` : ''
            const message = `You have tipped ${targetStr}${toTokens(amount)}.`
            commandConfirmation({ id: activeChat.id, message, tip: true })
        })
        .catch(({ message }: Error) => {
            commandConfirmation({ id: activeChat.id, error: message })
        })
}

async function tipForCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: i18n.t('chat.tipCommandWithoutPermission') })
        return
    }
    // grab the model name from the second word
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug || !isNaN(Number(slug))) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a model username.',
        })
        return
    }
    return tipCmd({ ...payload, slug, isTipFor: true })
}

async function shareTipCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({ message: i18n.t('chat.tipCommandWithoutPermission') })
        return
    }
    if (!activeChat) return
    const slug = activeChat.slug ?? idToSlug[activeChat.id]
    if (!slug) return
    // safely grab grab tip amount if there is one
    const getAmount = (): [number?, string?] => {
        try {
            return [Number(args[1]), null]
        } catch (_e) {
            return [null, 'You must supply a number for the token amount.']
        }
    }
    const [amount, error] = getAmount()
    if (!(amount > 0)) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must supply a positive token amount.',
        })
        return
    }
    if (error) {
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    // grab the target from the second word
    const target = args[2]?.toString()?.toLowerCase()
    if (!target) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a share code or model username.',
        })
        return
    }
    // grab the optional message from the third word
    const message = args[3]?.toString()?.toLowerCase() ?? ''
    return sendShareTip(slug, amount, target, message).catch(
        ({ message }: Error) => {
            commandConfirmation({ id: activeChat.id, message, shareTip: true })
        },
    )
}

function quickBuyCmd({
    args,
    chatType,
    activeChat,
    isPremium,
}: CommandPayload) {
    if (!isPremium) {
        openError({ message: i18n.t('chat.quickbuyCommandWithoutPermission') })
        return
    }
    const { slug } = activeChat
    const tokenVolume = args[1]
    if (!tokenVolume) {
        return getTokenPurchaseInfo().then(({ config }) => {
            inChatQuickBuyDialog({
                ...config,
                location: {
                    slug,
                    type:
                        chatType === FCTYPE_CMESG
                            ? 'room'
                            : chatType === FCTYPE_PMESG
                              ? 'message'
                              : undefined,
                },
            })
        })
    }
    return tokensQualifyPurchase({
        chat_command_purchase: 1,
        volume: tokenVolume,
        volume_type: 'token',
    })
}

async function toStorageCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({
            message: i18n.t('chat.tokenStorageCommandWithoutPermission'),
        })
        return
    }
    const tokenVolume = Number(args[1])
    if (!tokenVolume) return
    const { message, errmesg } = await shiftTokenStorage(tokenVolume)
    if (errmesg) {
        commandConfirmation({ id: activeChat.id, error: errmesg })
        return
    }
    commandConfirmation({ id: activeChat.id, message })
}

async function fromStorageCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({
            message: i18n.t('chat.tokenStorageCommandWithoutPermission'),
        })
        return
    }
    const tokenVolume = Number(args[1])
    if (!tokenVolume) return
    const { message, errmesg } = await shiftTokenStorage(-tokenVolume)
    if (errmesg) {
        commandConfirmation({ id: activeChat.id, error: errmesg })
        return
    }
    commandConfirmation({ id: activeChat.id, message })
}

type CommandConfirmationProps = {
    id: number
    error?: string
    message?: string
    shareTip?: boolean
    tip?: boolean
}

export function commandConfirmation(payload: CommandConfirmationProps) {
    const { id, error, message, shareTip: shareTipConfirmation, tip } = payload
    const text = error
        ? error.slice(-1) === '.'
            ? `Error: ${error}`
            : `Error: ${error}.`
        : message
    const msg: Message = {
        text,
        timestamp: Date.now(),
        confirmation: !error,
        shareTipConfirmation,
        system: true,
        tip: tip && !error,
        error,
    }

    const quickReplyOpen = isQuickReplyOpen(id)
    const floatingRoom = MODALSTATE.floating_room
    const roomView = floatingRoom.show && floatingRoom.state.roomView
    if (roomView === RoomView.Room) {
        setRoomMessagesState(msg, id)
    }
    if (roomView === RoomView.PM || quickReplyOpen) {
        setPMChatTextState(id, msg)
    }
}
